import { FC, useMemo, useState } from "react";
import { BaseScreen } from "../../components/BaseScreen";
import Select from "../../components/Select";
import { useTranslations } from "../../hooks/useTranslations.ts";
import { translationKeys } from "../../translations/translationKeys.ts";
import { REGIONS, getRegionForCountry } from "../../utils/countriesConfig";

interface RegionSelectScreenProps {
  lang: string;
  onCountrySelected: (country: { value: string; label: string }) => void;
  onNonUKCountry: (region: string) => void;
}

export const RegionSelectScreen: FC<RegionSelectScreenProps> = ({ lang, onCountrySelected, onNonUKCountry }) => {
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const { translate } = useTranslations();

  const translatedCountryNames = translate(translationKeys.REGION_SCREEN_COUNTRY_NAMES);

  const countryOptionsList = useMemo(() => {
    if (!translatedCountryNames || translatedCountryNames.startsWith("!!")) {
      return [];
    }
    return translatedCountryNames.split(",").map((entry) => {
      const [code, ...nameParts] = entry.split("=");
      return {
        value: (code || "").trim(),
        label: nameParts.join("=").trim(),
      };
    }).filter((c) => c.value && c.label)
      .sort((a, b) => a.label.localeCompare(b.label, lang));
  }, [translatedCountryNames, lang]);

  const selectOptions = countryOptionsList.map((c) => ({ key: c.value, value: c.label }));

  return (
    <BaseScreen
      formTitle={translate(translationKeys.REGION_SCREEN_TITLE)}
      formSubtitle={translate(translationKeys.REGION_SCREEN_SUBTITLE)}
      footerBtnLabel={translate(translationKeys.REGION_SCREEN_FOOTER_BUTTON_LABEL)}
      footerBtnOnClick={() => {
        const country = countryOptionsList.find((c) => c.value === selectedCountry);
        if (country) {
          const region = getRegionForCountry(country.value, lang);
          if (region === REGIONS.UK) {
            onCountrySelected(country);
          } else {
            onNonUKCountry(region);
          }
        }
      }}
      footerBtnDisabled={!selectedCountry}
      footerBtnLoading={false}
    >
      <Select
        label={translate(translationKeys.REGION_SCREEN_COUNTRY_LABEL)}
        value={selectedCountry}
        options={selectOptions}
        placeholder={translate(translationKeys.REGION_SCREEN_COUNTRY_PLACEHOLDER)}
        updateFieldValue={setSelectedCountry}
        triggerFieldChange={() => {}}
        fullWidth
      />
      <div style={{ marginTop: 16 }}>
        {translate(translationKeys.REGION_SCREEN_DISCLAIMER)}
      </div>
    </BaseScreen>
  );
};
